import type { PriceIndexKey, ProgressBillingApprovalStatus } from "../../types";

/** Laravel decimal cast'i string döner; para gösterimi için tr-TR formatına çevirir. */
export function formatMoney(value: string | number | null | undefined): string {
  const n = typeof value === "string" ? parseFloat(value) : (value ?? 0);
  if (!isFinite(n)) return "0,00";
  return n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Miktar (decimal:4) gösterimi — sondaki sıfırları atar. */
export function formatQuantity(value: string | number | null | undefined): string {
  const n = typeof value === "string" ? parseFloat(value) : (value ?? 0);
  if (!isFinite(n)) return "0";
  return n.toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 4 });
}

/** Axios hata nesnesinden backend mesajını çeker; 422'de alan bazlı ilk mesaj genel mesajdan önce gelir. */
export function getBackendError(err: unknown): string | null {
  const e = err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };
  if (e.response?.data?.errors) {
    const first = Object.values(e.response.data.errors).flat()[0];
    if (first) return first;
  }
  if (e.response?.data?.message) return e.response.data.message;
  return null;
}

/** Y-m dönemini "Temmuz 2026" benzeri etikete çevirir. */
export function formatPeriod(period: string | null | undefined): string {
  if (!period) return "-";
  const [year, month] = period.split("-");
  if (!year || !month) return period;
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString("tr-TR", { year: "numeric", month: "long" });
}

/** ISO veya "Y-m-d H:i:s" tarihi tr-TR kısa tarihe çevirir (örn. paid_at rozeti). */
export function formatBillingDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "-";
  const normalized = dateStr.length === 10 ? `${dateStr}T00:00:00` : dateStr.replace(" ", "T");
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("tr-TR");
}

export const BILLING_STATUS_META: Record<
  ProgressBillingApprovalStatus,
  { label: string; color: string }
> = {
  draft: { label: "Taslak", color: "bg-slate-100 text-slate-700 border-slate-200" },
  pending: { label: "Onay Bekliyor", color: "bg-amber-100 text-amber-700 border-amber-200" },
  submitted: { label: "İdareye Sunuldu", color: "bg-amber-100 text-amber-700 border-amber-200" },
  approved: { label: "Onaylandı", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  accrued: { label: "Tahakkuk Etti", color: "bg-blue-100 text-blue-700 border-blue-200" },
  rejected: { label: "Reddedildi", color: "bg-red-100 text-red-700 border-red-200" },
};

/** K-4 endeks anahtarı etiketleri (parantez içi katsayı eşleşmesi). */
export const PRICE_INDEX_KEY_LABELS: Record<PriceIndexKey, string> = {
  genel: "Yİ-ÜFE Genel (a)",
  iscilik: "İşçilik (c)",
  cimento: "Çimento (b1)",
  demir_celik: "Demir / Çelik (b2)",
  akaryakit: "Akaryakıt (b3)",
  yakit: "Yakıt",
  agac: "Ağaç / Kereste (b4)",
  diger_malzeme: "Diğer Malzeme (b6)",
  makine: "Makine (b5)",
};

export const SUBCONTRACT_STATUS_META: Record<string, { label: string; color: string }> = {
  active: { label: "Aktif", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  completed: { label: "Tamamlandı", color: "bg-blue-100 text-blue-700 border-blue-200" },
  terminated: { label: "Feshedildi", color: "bg-red-100 text-red-700 border-red-200" },
};
